import React, { useEffect, useState, useRef } from "react";
import { RadioButton, Tooltip } from "@vibe/core";
import SettingsDropdown from "../../SettingsDropdown";

interface SubitemsSettingsProps {
  boardSettingsData: any;
  onSubitemPreferenceChange: (preference: string, excludedBoardIds?: string[]) => void;
  options: { value: string; label: string }[];
}

const SubitemsSettings = ({ boardSettingsData, onSubitemPreferenceChange, options }: SubitemsSettingsProps) => {
  const [selectedSubitemPreference, setSelectedSubitemPreference] = useState("");
  const [selectedBoards, setSelectedBoards] = useState<{ label: string; value: string | number }[]>([]);
  const previousBoardsRef = useRef<{ label: string; value: string | number }[]>([]);

  useEffect(() => {
    if (boardSettingsData?.subitems?.preference) {
      setSelectedSubitemPreference(boardSettingsData?.subitems?.preference);
    }

    if (boardSettingsData?.subitems?.excluded_board_ids && options) {
      const boards = options.filter((option) => boardSettingsData?.subitems?.excluded_board_ids?.includes(option.value));
      if (JSON.stringify(boards) !== JSON.stringify(previousBoardsRef.current)) {
        setSelectedBoards(boards);
        previousBoardsRef.current = boards;
      }
    }
  }, [boardSettingsData, options]);

  const handleRadioButtonSelect = (event: React.ChangeEvent<HTMLInputElement | null>) => {
    if (event.target) {
      setSelectedSubitemPreference(event.target.value);
      setSelectedBoards([]); // Clear selected boards when radio button is selected
      onSubitemPreferenceChange(event.target.value, []);
    }
  };

  const handleDropdownChange = (selectedOptions: any) => {
    if (!selectedOptions) {
      setSelectedBoards([]);
      setSelectedSubitemPreference("sync_to_all");
      onSubitemPreferenceChange("sync_to_all", []);
      return;
    }

    setSelectedBoards(selectedOptions);

    if (selectedOptions.length > 0) {
      setSelectedSubitemPreference(""); // Deselect radio buttons
      onSubitemPreferenceChange(
        "sync_to_all_except",
        selectedOptions.map((option: any) => option.value)
      );
    } else {
      // If no boards are selected, default to sync_all_subitems
      setSelectedSubitemPreference("sync_to_all");
      onSubitemPreferenceChange("sync_to_all", []);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Tooltip content="This will enable subitems to be created in shared items in this board, and added to shared items on other boards. You can override this setting for specific items in the item settings.">
          <RadioButton
            checked={selectedSubitemPreference === "sync_to_all"}
            text="When subitems are created in shared items in this board, add them to shared items on other boards"
            name="board-subitems-radio-button"
            onSelect={(event) => handleRadioButtonSelect(event)}
            value="sync_to_all"
          />
        </Tooltip>
        <Tooltip content="This will disable subitems to be created in shared items in this board, and added to shared items on other boards. You can override this setting for specific items in the item settings.">
          <RadioButton
            checked={selectedSubitemPreference === "sync_to_none"}
            text="Never share subitems created on this board with other boards"
            name="board-subitems-radio-button"
            onSelect={(event) => handleRadioButtonSelect(event)}
            value="sync_to_none"
          />
        </Tooltip>
      </div>

      <SettingsDropdown
        label="Sync subitems to all linked boards except:"
        placeholder="Select Board"
        options={options}
        onChange={handleDropdownChange}
        multi={true}
        defaultValue={selectedBoards}
      />
    </div>
  );
};

export default SubitemsSettings;
